import React, { useState, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  CircularProgress,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import API from "../../../axios/instances/API";
import styled from "styled-components";

const StyledTextField = styled(TextField)`
  .MuiInputBase-input {
    font-size: 15px;
    font-weight: 500;
  }
`;

const CustomChip = styled.div`
  padding: 0px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background-color: ${(props) => (props.$isRetail ? "#337ab7" : "slategrey")};

  p {
    color: #eeeeee;
    font-size: 10px;

    font-weight: 600;
  }
`;

const CustomRenderOption = ({ option, ...props }) => (
  <div
    {...props}
    style={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <p style={{ color: "black", fontWeight: "400" }}>{option.name}</p>
    <CustomChip $isRetail={option.is_retail}>
      <p>{!option.is_retail ? "Supplier" : "Employer"}</p>
    </CustomChip>
  </div>
);

const ACCESS_LEVELS = {
  SUPERVISOR: "Supervisor",
  ADMIN: "Admin",
};

const SearchForCompanyForm = ({ dispatch, ACTIONS, signupState }) => {
  const { companyInfo } = signupState;
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [accessLevel, setAccessLevel] = useState(ACCESS_LEVELS.ADMIN);
  const [locations, setLocations] = useState("");
  const [locationComboValue, setLocationComboValue] = useState([]);
  const [requestLoading, setRequestLoading] = useState(false);
  const [search, setSearch] = useState("");

  const isLoading =
    open && options.length === 0 && search.length > 2 && requestLoading;
  const locationId = companyInfo?.id || null;

  useEffect(() => {
    if (search.length < 3) {
      return setOptions([]);
    }
    const searchCompany = async () => {
      try {
        setRequestLoading(true);
        const { data } = await API.get(
          `common/company/search/?search=${search}&include_vendors=1`,
        );

        setOptions(data.companies);
        setRequestLoading(false);
      } catch (error) {
        setOptions([]);
      }
    };
    searchCompany();
  }, [search]);

  useEffect(() => {
    if (!locationId) return undefined;

    const getLocations = async () => {
      try {
        const { data } = await API.get(
          `dashboard/signup/company/locations/?company_id=${locationId}`,
        );
        setLocations(data.locations);
      } catch (error) {
        // setErrorAlert(FEEDBACK_MESSAGES.GENERIC_ERROR);
      }
    };

    getLocations();
  }, [locationId]);

  const handleAddCompany = (_, selectedCompany) => {
    if (selectedCompany) {
      dispatch({ type: ACTIONS.UPDATE_WORKPLACE, payload: selectedCompany });
    } else {
      setAccessLevel(ACCESS_LEVELS.ADMIN);
      dispatch({ type: ACTIONS.REMOVE_WORKPLACE });
    }
  };

  const handleRadioChange = (e) => {
    setAccessLevel(e.target.value);
  };

  const companySelected = companyInfo && Object.keys(companyInfo).length > 0;

  const isContinueDisabled = () => {
    if (!companySelected) return true;

    if (!companyInfo.is_retail) return false;

    if (
      accessLevel === ACCESS_LEVELS.SUPERVISOR &&
      locationComboValue.length === 0
    ) {
      return true;
    }

    return false;
  };

  return (
    <>
      <Autocomplete
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        freeSolo={true}
        onClose={() => {
          setOpen(false);
          setOptions([]);
        }}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        onInputChange={(e) =>
          e.target.innerText ? setSearch(e.target.innerText) : setSearch("")
        }
        onChange={handleAddCompany}
        getOptionLabel={(option) => option.name}
        options={options}
        loading={isLoading}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        renderOption={(props, option) => (
          <CustomRenderOption option={option} {...props} />
        )}
        noOptionsText="No Companies Found"
        renderInput={(params) => (
          <StyledTextField
            {...params}
            label=""
            variant="outlined"
            style={{ fontSize: 14, fontWeight: "600" }}
            placeholder="Search for your company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? (
                    <CircularProgress sx={{ color: "black" }} size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
      {companySelected && companyInfo.is_retail && (
        <div style={{ marginTop: 16 }}>
          <FormControl component="fieldset">
            <Typography variant="subtitle2" color="textSecondary">
              Are you an Admin or Supervisor at {companyInfo.name} ?
            </Typography>
            <RadioGroup row value={accessLevel} onChange={handleRadioChange}>
              <FormControlLabel
                value={ACCESS_LEVELS.ADMIN}
                control={<Radio />}
                label={ACCESS_LEVELS.ADMIN}
              />
              <FormControlLabel
                value={ACCESS_LEVELS.SUPERVISOR}
                control={<Radio />}
                label={ACCESS_LEVELS.SUPERVISOR}
              />
            </RadioGroup>
          </FormControl>
        </div>
      )}
      {accessLevel === ACCESS_LEVELS.SUPERVISOR &&
        companySelected &&
        companyInfo.is_retail && (
          <>
            <Typography
              sx={{ mt: 3, mb: 4 }}
              variant="subtitle2"
              color="textSecondary"
            >
              Please select the location(s) you supervise
            </Typography>
            <Autocomplete
              size="small"
              options={locations}
              multiple
              noOptionsText="No Locations Available"
              getOptionLabel={(option) => option.name}
              onChange={(_, newValue) => setLocationComboValue(newValue || [])}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Location(s) To Add"
                  variant="outlined"
                />
              )}
            />
          </>
        )}
      {companySelected && (
        <Button
          color="primary"
          variant="contained"
          sx={{ mt: 10 }}
          fullWidth
          disabled={isContinueDisabled()}
          onClick={() =>
            dispatch({
              type: ACTIONS.SUBMIT_WORKPLACE,
              payload: {
                accessLevel,
                company: options[0],
                location:
                  accessLevel === ACCESS_LEVELS.ADMIN
                    ? null
                    : locationComboValue,
              },
            })
          }
        >
          Continue
        </Button>
      )}
      {!companySelected && (
        <Button
          fullWidth
          sx={{ mt: 3, mb: -2 }}
          onClick={() => dispatch({ type: ACTIONS.USER_CANT_FIND_COMPANY })}
          variant="text"
          color="primary"
        >
          Can't Find Your Company? Create one here!
        </Button>
      )}
    </>
  );
};

export default SearchForCompanyForm;
